/*
  Client-side product-tag filter for the Shop All pill row (owner request 2026-09-19).
  Deliberately not Shopify's native `filter.p.tag` faceting -- that needs a Product tag
  filter enabled per collection in Search & Discovery, which this store doesn't have
  configured. Instead each grid item carries its own tags (data-cc-tags, set in
  main-collection-product-grid.liquid) and this just shows/hides them.

  Inline style, not the `hidden` attribute: component-cc-shop.css sets an explicit
  `display: flex` on `.cc-shop #product-grid .grid__item` (for the equal-card-height fix),
  which outranks the browser's built-in `[hidden] { display: none }` rule -- an inline
  style is the one thing guaranteed to beat that.

  URL (2026-09-24): the active chip lives in `?filter=<id>`, where <id> is the chip block's
  id in templates/collection.json (weight | breathing | calm | books | classroom -- the
  mockup's ids), or the chip's tag handleized. Category links elsewhere on the site
  (footer, Find Your Tool, ...) point at /collections/shop-all?filter=<id> and land
  pre-filtered. Chip clicks rewrite the param with replaceState so sort_by etc. survive.

  Dawn's sort control (facets.js) re-renders #ProductGridContainer and pushes a URL built
  only from the sort form, dropping `filter`; a MutationObserver re-applies the filter and
  puts the param back. On mobile the chip list is hidden (owner request 2026-09-19), so an
  active filter also shows a removable "Showing: X" pill (.cc-shop-chips__clear).
*/

class CcShopChips extends HTMLElement {
  connectedCallback() {
    this.gridSelector = this.dataset.gridSelector || '#product-grid';
    this.buttons = Array.from(this.querySelectorAll('[data-filter-tag]'));
    this.countEl = this.querySelector('#ProductCount');
    this.clearEl = this.querySelector('[data-cc-chip-clear]');
    this.clearLabelEl = this.querySelector('[data-cc-chip-clear-label]');
    this.active = this.buttons[0] || null;

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.select(button, true));
    });
    if (this.clearEl && this.buttons[0]) {
      this.clearEl.addEventListener('click', () => this.select(this.buttons[0], true));
    }

    this.onPopState = () => this.applyFromUrl();
    window.addEventListener('popstate', this.onPopState);

    const container = document.getElementById('ProductGridContainer');
    if (container) {
      this.observer = new MutationObserver(() => {
        if (!this.active) return;
        this.filter(this.active.dataset.filterTag || '');
        this.writeUrl();
      });
      this.observer.observe(container, { childList: true });
    }

    this.applyFromUrl();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    if (this.observer) this.observer.disconnect();
  }

  applyFromUrl() {
    const wanted = (new URLSearchParams(window.location.search).get('filter') || '').trim().toLowerCase();
    const match = wanted ? this.buttons.find((button) => this.idsFor(button).includes(wanted)) : null;
    const target = match || this.buttons[0];
    if (target) this.select(target, false);
  }

  idsFor(button) {
    const handle = (text) =>
      (text || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    return [(button.dataset.filterId || '').toLowerCase(), handle(button.dataset.filterTag)].filter(Boolean);
  }

  select(selected, updateUrl) {
    this.active = selected;
    this.buttons.forEach((button) => {
      const isActive = button === selected;
      button.classList.toggle('cc-shop-chips__chip--active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    const tag = selected.dataset.filterTag || '';
    this.filter(tag);
    this.updateClear(tag === '' ? '' : selected.textContent.trim());
    if (updateUrl) this.writeUrl();
  }

  writeUrl() {
    const url = new URL(window.location.href);
    const tag = this.active ? this.active.dataset.filterTag || '' : '';
    if (tag === '') {
      url.searchParams.delete('filter');
    } else {
      url.searchParams.set('filter', this.idsFor(this.active)[0]);
    }
    if (url.href !== window.location.href) history.replaceState(history.state, '', url.href);
  }

  updateClear(label) {
    if (!this.clearEl) return;
    this.clearEl.hidden = label === '';
    if (this.clearLabelEl) this.clearLabelEl.textContent = label;
  }

  filter(tag) {
    const grid = document.querySelector(this.gridSelector);
    if (!grid) return;
    const items = Array.from(grid.children).filter((item) => item.hasAttribute('data-cc-tags'));
    let visible = 0;
    items.forEach((item) => {
      const tags = item.getAttribute('data-cc-tags').split('|');
      const match = tag === '' || tags.includes(tag);
      item.style.display = match ? '' : 'none';
      if (match) visible += 1;
    });
    this.updateCount(visible);
  }

  updateCount(count) {
    if (!this.countEl) return;
    this.countEl.textContent = count === 1 ? '1 product' : `${count} products`;
  }
}

customElements.define('cc-shop-chips', CcShopChips);
